import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AttendanceDayDetailModal from "./AttendanceDayDetailModal";
import { RepeatingTimerIcon } from "../../../../shared/components/ui/TimerIcon";

import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

const WEEK_DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const AREA_COLORS = [
  { main: "#34C759", soft: "rgba(52,199,89,0.22)", border: "rgba(52,199,89,0.55)" },
  { main: "#16B8C7", soft: "rgba(22,184,199,0.22)", border: "rgba(22,184,199,0.55)" },
  { main: "#F59E0B", soft: "rgba(245,158,11,0.22)", border: "rgba(245,158,11,0.55)" },
  { main: "#6366F1", soft: "rgba(99,102,241,0.22)", border: "rgba(99,102,241,0.55)" },
  { main: "#EC4899", soft: "rgba(236,72,153,0.20)", border: "rgba(236,72,153,0.52)" },
  { main: "#10B981", soft: "rgba(16,185,129,0.20)", border: "rgba(16,185,129,0.52)" },
  { main: "#EF4444", soft: "rgba(239,68,68,0.18)", border: "rgba(239,68,68,0.50)" },
  { main: "#8B5CF6", soft: "rgba(139,92,246,0.20)", border: "rgba(139,92,246,0.52)" },
];

const PILL_SX = {
  height: "5vh",
  minHeight: 36,
  bgcolor: "neutral.50",
  borderRadius: "50px",
  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
};

const SELECT_SX = {
  ...PILL_SX,
  minWidth: 160,
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    fontSize: 13,
    fontWeight: 500,
    py: 0,
    display: "flex",
    alignItems: "center",
  },
};

const EMPLOYEE_COL_SX = {
  flex: "0 0 18%",
  minWidth: "18%",
  maxWidth: "18%",
};

const TOTAL_COL_SX = {
  flex: "0 0 11%",
  minWidth: "11%",
  maxWidth: "11%",
};

const TOTAL_VALUE_BOX_SX = {
  width: "88%",
  ml: "auto",
  boxSizing: "border-box",
};

const DAYS_COL_SX = {
  flex: "1 1 0",
  minWidth: 0,
};

const TABLE_SCROLLBAR_WIDTH = 10;

const TABLE_SIDE_PADDING_SX = {
  pl: 0.5,
  pr: `calc(${TABLE_SCROLLBAR_WIDTH}px)`,
  boxSizing: "border-box",
};

const TABLE_ROW_SX = {
  display: "flex",
  gap: 1,
  width: "100%",
  alignItems: "stretch",
};

function pad2(n) {
  return String(n).padStart(2, "0");
}

function fmtYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function startOfWeekMonday(date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;

  next.setDate(next.getDate() + diff);
  next.setHours(0, 0, 0, 0);

  return next;
}

function normalizeAreaId(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

function getEmployeeId(row) {
  return Number(row?.employeeId || row?.id || 0);
}

function getRowDateKey(row) {
  return String(row?.ymd || row?.date || row?.workDate || "").slice(0, 10);
}

function getAreaColor(areaId) {
  const id = Number(areaId || 0);
  if (!Number.isFinite(id) || id <= 0) return AREA_COLORS[0];

  return AREA_COLORS[Math.abs(id) % AREA_COLORS.length];
}

function formatMinutesLabel(minutes) {
  const total = Math.max(0, Number(minutes) || 0);
  const h = Math.floor(total / 60);
  const m = total % 60;

  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function buildWeekHeaders(selectedDate) {
  const weekStart = startOfWeekMonday(selectedDate);

  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(weekStart, index);

    return {
      ymd: fmtYMD(date),
      label: `${WEEK_DAYS[index]} ${date.getDate()}`,
    };
  });
}

function getRowAreas(row) {
  const map = new Map();

  if (Array.isArray(row?.areas)) {
    for (const area of row.areas) {
      const areaId = normalizeAreaId(area?.areaId ?? area?.id);
      if (!areaId) continue;

      map.set(areaId, {
        areaId,
        areaName: area?.areaName || area?.name || area?.nombre || `Área ${areaId}`,
      });
    }
  }

  const activeAreaId = normalizeAreaId(row?.activeAreaId);
  if (activeAreaId && !map.has(activeAreaId)) {
    map.set(activeAreaId, {
      areaId: activeAreaId,
      areaName: row?.activeAreaName || row?.areaName || row?.area || `Área ${activeAreaId}`,
    });
  }

  const directAreaId = normalizeAreaId(row?.areaId ?? row?.area_id);
  if (directAreaId && !map.has(directAreaId)) {
    map.set(directAreaId, {
      areaId: directAreaId,
      areaName: row?.areaName || row?.area_name || row?.area || `Área ${directAreaId}`,
    });
  }

  return [...map.values()];
}

function getCellConfigForArea(row, areaId, now = Date.now()) {
  if (!row) {
    return {
      label: "",
      minutes: 0,
      clickable: false,
      bgcolor: "rgba(255,255,255,0.18)",
      borderColor: "rgba(96,96,113,0.12)",
      color: "neutral.600",
    };
  }

  const rowAreas = getRowAreas(row);
  const normalizedAreaId = normalizeAreaId(areaId);

  const belongsToArea = rowAreas.some((area) => area.areaId === normalizedAreaId);

  if (!belongsToArea) {
    return {
      label: "",
      minutes: 0,
      clickable: false,
      bgcolor: "rgba(255,255,255,0.12)",
      borderColor: "rgba(96,96,113,0.08)",
      color: "neutral.600",
    };
  }

  const latestStatus = String(row?.latestStatus || "").toUpperCase();
  const attendanceStatus = String(row?.attendanceStatus || "").toUpperCase();

  const hasAttendance =
    attendanceStatus === "ASISTENCIA" ||
    latestStatus === "OPEN" ||
    latestStatus === "CLOSED" ||
    Boolean(row?.firstClockInAt) ||
    Boolean(row?.lastClockOutAt);

  const isOpen =
    latestStatus === "OPEN" ||
    (Boolean(row?.firstClockInAt) && !row?.lastClockOutAt);

  const isClosed =
    latestStatus === "CLOSED" ||
    (Boolean(row?.firstClockInAt) && Boolean(row?.lastClockOutAt));

  const isAbsent =
    latestStatus === "ABSENT" ||
    attendanceStatus === "AUSENCIA";

  const isFree =
    latestStatus === "FRANCO" ||
    attendanceStatus === "FRANCO";
  const hasWarning = Boolean(row?.hasPendingUnusualSession);

  const activeAreaId = normalizeAreaId(row?.activeAreaId);
  const hasMultipleAreas = rowAreas.length > 1;

  const isActiveArea =
    !activeAreaId ||
    activeAreaId === normalizedAreaId ||
    !hasMultipleAreas;

  if (hasAttendance && !isActiveArea) {
    return {
      label: "",
      minutes: 0,
      clickable: true,
      bgcolor: "rgba(76, 175, 80, 0.20)",
      borderColor: "rgba(56, 142, 60, 0.55)",
      color: "success.main",
    };
  }

  if (isAbsent) {
    return {
      label: "",
      minutes: 0,
      clickable: true,
      bgcolor: "rgba(244, 67, 54, 0.76)",
      borderColor: "rgba(183, 28, 28, 0.72)",
      color: "logoblanco",
    };
  }

  if (isOpen) {
    const clockInMs = row?.firstClockInAt ? new Date(row.firstClockInAt).getTime() : NaN;
    const elapsedMinutes = Number.isFinite(clockInMs)
      ? Math.max(0, Math.floor((now - clockInMs) / 60000))
      : Math.max(0, Number(row?.openElapsedMinutes) || 0);
    return {
      label: formatMinutesLabel(elapsedMinutes),
      minutes: Math.max(0, Number(row?.workedMinutes) || 0),
      clickable: true,
      open: true,
      bgcolor: "rgba(33, 150, 243, 0.88)",
      borderColor: hasWarning ? "warning.main" : "rgba(21, 101, 192, 0.82)",
      color: "logoblanco",
      warning: hasWarning,
    };
  }

  if (isClosed) {
    const minutes = Math.max(0, Number(row?.workedMinutes) || 0);

    return {
      label: formatMinutesLabel(minutes),
      minutes,
      clickable: true,
      bgcolor: hasWarning ? "rgba(245, 124, 0, 0.88)" : "rgba(76, 175, 80, 0.88)",
      borderColor: hasWarning ? "rgba(230, 81, 0, 0.9)" : "rgba(46, 125, 50, 0.75)",
      color: "logoblanco",
      warning: hasWarning,
    };
  }

  if (isFree) {
    return {
      label: "",
      minutes: 0,
      clickable: false,
      bgcolor: "rgba(255,255,255,0.18)",
      borderColor: "rgba(96,96,113,0.12)",
      color: "neutral.600",
    };
  }

  return {
    label: row?.attendanceDisplayStatus || "",
    minutes: 0,
    clickable: true,
    bgcolor: "rgba(255,255,255,0.18)",
    borderColor: "rgba(96,96,113,0.12)",
    color: "primary.900",
  };
}

function buildEmployeeAreaRows(rowsSource, weekHeaders, areaFilterId) {
  const employeeMap = new Map();

  for (const row of rowsSource) {
    const employeeId = getEmployeeId(row);
    if (!employeeId) continue;

    if (!employeeMap.has(employeeId)) {
      employeeMap.set(employeeId, {
        employeeId,
        fullName: String(row?.fullName || row?.nombre || `Empleado #${employeeId}`).trim(),
        dni: String(row?.dni || "").trim(),
        rowsByDay: new Map(),
        areasById: new Map(),
        firstRow: row,
      });
    }

    const employee = employeeMap.get(employeeId);
    const ymd = getRowDateKey(row);

    if (ymd) {
      employee.rowsByDay.set(ymd, row);
    }

    for (const area of getRowAreas(row)) {
      employee.areasById.set(area.areaId, area);
    }
  }

  const virtualRows = [];

  for (const employee of employeeMap.values()) {
    if (employee.areasById.size === 0) {
      continue;
    }

    for (const area of employee.areasById.values()) {
      if (areaFilterId && String(area.areaId) !== String(areaFilterId)) {
        continue;
      }

      const totalMinutes = weekHeaders.reduce((sum, day) => {
        const row = employee.rowsByDay.get(day.ymd) || null;
        const cell = getCellConfigForArea(row, area.areaId);
        return sum + Number(cell.minutes || 0);
      }, 0);

      virtualRows.push({
        ...employee,
        virtualKey: `${employee.employeeId}-${area.areaId}`,
        areaId: area.areaId,
        areaName: area.areaName,
        areaColor: getAreaColor(area.areaId),
        totalMinutes,
      });
    }
  }

  return virtualRows;
}

function groupRowsByArea(rows) {
  const map = new Map();

  for (const row of rows) {
    const areaId = normalizeAreaId(row.areaId);
    if (!areaId) continue;

    if (!map.has(areaId)) {
      map.set(areaId, {
        areaId,
        areaName: row.areaName,
        areaColor: row.areaColor || getAreaColor(areaId),
        totalMinutes: 0,
        rows: [],
      });
    }

    const group = map.get(areaId);
    group.totalMinutes += Number(row.totalMinutes || 0);
    group.rows.push(row);
  }

  return [...map.values()]
    .map((group) => ({
      ...group,
      rows: [...group.rows].sort((a, b) => {
        const diff = Number(b.totalMinutes || 0) - Number(a.totalMinutes || 0);
        if (diff !== 0) return diff;
        return String(a.fullName || "").localeCompare(String(b.fullName || ""), "es");
      }),
    }))
    .sort((a, b) => String(a.areaName || "").localeCompare(String(b.areaName || ""), "es"));
}

function buildWeeklyTotals(rows, weekHeaders) {
  const byDay = weekHeaders.map((day) => {
    return rows.reduce((sum, employee) => {
      const dayRow = employee.rowsByDay.get(day.ymd) || null;
      const cell = getCellConfigForArea(dayRow, employee.areaId);
      return sum + Number(cell.minutes || 0);
    }, 0);
  });

  return {
    byDay,
    totalMinutes: byDay.reduce((sum, minutes) => sum + minutes, 0),
  };
}

function normalizeWeeklyTotals(totals, weekHeaders, fallback) {
  if (!totals || !Array.isArray(totals.byDay)) {
    return fallback;
  }

  const minutesByDay = new Map(
    totals.byDay.map((day) => [
      String(day?.ymd || "").slice(0, 10),
      Math.max(0, Number(day?.workedMinutes ?? day?.minutes ?? 0) || 0),
    ])
  );

  const byDay = weekHeaders.map((day) => minutesByDay.get(day.ymd) || 0);
  const totalMinutes = Number.isFinite(Number(totals.totalMinutes))
    ? Math.max(0, Number(totals.totalMinutes))
    : byDay.reduce((sum, minutes) => sum + minutes, 0);

  return {
    byDay,
    totalMinutes,
  };
}

function AttendanceToolbar({ weekLabel, onPrevWeek, onNextWeek }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{
        px: 2,
        py: 0.5,
        bgcolor: "neutral.50",
        borderRadius: "20px",
        boxShadow: "inset 0px 0px 4px rgba(165, 165, 178, 1)",
      }}
    >
      <IconButton onClick={onPrevWeek} sx={{ width: 32, height: 32 }}>
        <ChevronLeftIcon sx={{ fontSize: 28, color: "primary.900" }} />
      </IconButton>

      <Box
        sx={{
          px: 2.5,
          py: 0.75,
          bgcolor: "secondary.200",
          borderRadius: "33px",
          boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
          minWidth: 230,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>
          {weekLabel}
        </Typography>
      </Box>

      <IconButton onClick={onNextWeek} sx={{ width: 32, height: 32 }}>
        <ChevronRightIcon sx={{ fontSize: 28, color: "primary.900" }} />
      </IconButton>
    </Stack>
  );
}

function AttendanceViewSwitch({ value, onChange, sortDirection, onToggleSortDirection }) {
  const options = [
    { value: "area", label: "Áreas" },
    { value: "hours", label: "Horas" },
  ];

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        height: PILL_SX.height,
        minHeight: PILL_SX.minHeight,
        alignSelf: "stretch",
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          bgcolor: "neutral.200",
          borderRadius: "999px",
          p: 0.35,
          boxShadow: "inset 0px 0px 3px rgba(96,96,113,0.35)",
        }}
      >
        {options.map((option) => {
          const active = value === option.value;

          return (
            <Box
              key={option.value}
              onClick={() => onChange(option.value)}
              sx={{
                minWidth: 74,
                height: "100%",
                px: 1.3,
                py: 0,
                borderRadius: "999px",
                textAlign: "center",
                fontSize: 12,
                fontWeight: 900,
                color: active ? "logoblanco" : "primary.900",
                bgcolor: active ? "secondary.500" : "transparent",
                boxShadow: active ? "0px 4px 10px rgba(0,0,0,0.14)" : "none",
                cursor: "pointer",
                userSelect: "none",
                transition: "0.15s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  bgcolor: active ? "secondary.600" : "rgba(255,255,255,0.45)",
                },
              }}
            >
              {option.label}
            </Box>
          );
        })}
      </Box>

      {value === "hours" ? (
        <IconButton
          onClick={onToggleSortDirection}
          sx={{
            width: PILL_SX.minHeight,
            height: PILL_SX.minHeight,
            bgcolor: "neutral.200",
            border: "1px solid rgba(96,96,113,0.22)",
            boxShadow: "inset 0px 0px 3px rgba(96,96,113,0.25)",
            "&:hover": { bgcolor: "neutral.250" },
          }}
        >
          {sortDirection === "desc" ? (
            <ArrowDownwardIcon sx={{ fontSize: 18, color: "primary.900" }} />
          ) : (
            <ArrowUpwardIcon sx={{ fontSize: 18, color: "primary.900" }} />
          )}
        </IconButton>
      ) : null}
    </Stack>
  );
}

const ATTENDANCE_STATES = [
  {
    key: "finished",
    title: "Jornada finalizada",
    description: "El empleado registró su entrada y salida. No tiene advertencias pendientes.",
    label: "8h 00m",
    bgcolor: "rgba(76, 175, 80, 0.88)",
    borderColor: "rgba(46, 125, 50, 0.75)",
  },
  {
    key: "open",
    title: "Jornada en curso",
    description: "El empleado registró su entrada, pero todavía no su salida. El tiempo mostrado es provisional.",
    label: "3h 05m",
    bgcolor: "rgba(33, 150, 243, 0.88)",
    borderColor: "rgba(21, 101, 192, 0.82)",
    timer: true,
  },
  {
    key: "unusual",
    title: "Jornada inusual",
    description: "Existe un turno de más de 12 horas que requiere revisión.",
    label: "14h 01m",
    bgcolor: "rgba(245, 124, 0, 0.88)",
    borderColor: "rgba(230, 81, 0, 0.9)",
    warning: true,
  },
  {
    key: "open-unusual",
    title: "En curso e inusual",
    description: "El empleado continúa trabajando y su jornada supera las 12 horas.",
    label: "17h 18m",
    bgcolor: "rgba(33, 150, 243, 0.88)",
    borderColor: "warning.main",
    warning: true,
    timer: true,
  },
  {
    key: "empty",
    title: "Sin asistencia registrada",
    description: "No existen fichajes registrados para ese día.",
    label: "",
    bgcolor: "rgba(255,255,255,0.18)",
    borderColor: "rgba(96,96,113,0.12)",
    empty: true,
  },
];

function AttendanceStatesDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="attendance-states-title"
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 32px)", sm: 540 },
          maxWidth: 540,
          m: { xs: 2, sm: 3 },
          borderRadius: "22px",
          bgcolor: "neutral.50",
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 0.75 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography id="attendance-states-title" sx={{ fontSize: 20, fontWeight: 900, color: "primary.900" }}>
              Estados de asistencia
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 13, color: "neutral.700" }}>
              Conocé qué significa cada color e indicador de la grilla.
            </Typography>
          </Box>
          <Tooltip title="Cerrar">
            <IconButton onClick={onClose} aria-label="Cerrar" size="small" sx={{ mt: -0.5, mr: -0.75 }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: "12px !important", pb: 2.5 }}>
        <Stack spacing={1.25}>
          {ATTENDANCE_STATES.map((state) => (
            <Box
              key={state.key}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "104px minmax(0, 1fr)", sm: "126px minmax(0, 1fr)" },
                gap: { xs: 1.25, sm: 1.75 },
                alignItems: "center",
                p: 1,
                borderRadius: "13px",
                bgcolor: "neutral.100",
              }}
            >
              <Box
                role="img"
                aria-label={state.title}
                sx={{
                  height: 44,
                  borderRadius: "10px",
                  bgcolor: state.bgcolor,
                  border: "2px solid",
                  borderColor: state.borderColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  color: state.empty ? "neutral.600" : "#FFFFFF",
                  boxSizing: "border-box",
                }}
              >
                {state.warning ? <WarningRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} /> : null}
                {state.timer ? <RepeatingTimerIcon aria-hidden="true" size={18} style={{ display: "inline-flex" }} /> : null}
                {state.label ? (
                  <Typography component="span" sx={{ color: "inherit", fontSize: 11, fontWeight: 900, lineHeight: 1 }}>
                    {state.label}
                  </Typography>
                ) : null}
              </Box>

              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: "primary.900", lineHeight: 1.25 }}>
                  {state.title}
                </Typography>
                <Typography sx={{ mt: 0.3, fontSize: 12.5, color: "neutral.700", lineHeight: 1.35 }}>
                  {state.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        <Typography
          sx={{
            mt: 1.75,
            px: 1.25,
            py: 1,
            borderRadius: "10px",
            bgcolor: "neutral.100",
            color: "neutral.700",
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          Las jornadas inusuales pueden marcarse como resueltas después de revisarlas, sin modificar las horas registradas.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

export default function AttendanceSection({
  dailyRows = [],
  weeklyTotals: weeklyTotalsProp = null,
  selectedDate,
  weekLabel = "",
  onPrevWeek,
  onNextWeek,
  search = "",
  onSearch,
  branchId = "",
  onBranchId,
  areaId = "",
  onAreaId,
  branches = [],
  areas = [],
  loading = false,
  fetchError = "",
  onAttendanceSaved,
  onOpenExportPdf,
  exportingPdf = false,
}) {
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("area");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedDayRow, setSelectedDayRow] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [statesInfoOpen, setStatesInfoOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const weekHeaders = useMemo(() => buildWeekHeaders(selectedDate), [selectedDate]);

  const rowsSource = useMemo(
    () => (Array.isArray(dailyRows) ? dailyRows : []),
    [dailyRows]
  );

  const virtualRows = useMemo(
    () => buildEmployeeAreaRows(rowsSource, weekHeaders, areaId),
    [rowsSource, weekHeaders, areaId]
  );

  const areaGroups = useMemo(
    () => groupRowsByArea(virtualRows),
    [virtualRows]
  );

  const hourRows = useMemo(() => {
    return [...virtualRows].sort((a, b) => {
      const diff = Number(a.totalMinutes || 0) - Number(b.totalMinutes || 0);
      const sortedDiff = sortDirection === "asc" ? diff : -diff;

      if (sortedDiff !== 0) return sortedDiff;

      return String(a.fullName || "").localeCompare(String(b.fullName || ""), "es");
    });
  }, [virtualRows, sortDirection]);

  const localWeeklyTotals = useMemo(
    () => buildWeeklyTotals(virtualRows, weekHeaders),
    [virtualRows, weekHeaders]
  );

  const weeklyTotals = useMemo(
    () => normalizeWeeklyTotals(weeklyTotalsProp, weekHeaders, localWeeklyTotals),
    [weeklyTotalsProp, weekHeaders, localWeeklyTotals]
  );

  const handleEmployeeClick = (employee, dayRow = null) => {
    const employeeId = Number(employee?.employeeId || employee?.id);
    if (!employeeId) return;

    const baseEmployee = employee?.firstRow || {};
    const selectedRow = dayRow || employee?.rowsByDay?.get(fmtYMD(selectedDate)) || baseEmployee;

    navigate(`/rrhh/asistencia/${employeeId}`, {
      state: {
        employee: {
          ...baseEmployee,
          ...selectedRow,
          employeeId,
          fullName: employee?.fullName || selectedRow?.fullName,
          dni: employee?.dni || selectedRow?.dni,
        },
      },
    });
  };

  const handleAttendanceCellClick = (employee, day, dayRow = null) => {
    const employeeId = Number(employee?.employeeId || employee?.id);
    if (!employeeId || !day?.ymd) return;

    const baseEmployee = employee?.firstRow || {};

    setSelectedDayRow({
      ...baseEmployee,
      ...(dayRow || {}),
      employeeId,
      id: employeeId,
      fullName:
        employee?.fullName ||
        dayRow?.fullName ||
        baseEmployee?.fullName ||
        `Empleado ${employeeId}`,
      dni: employee?.dni || dayRow?.dni || baseEmployee?.dni || null,
      ymd: day.ymd,
      workDate: day.ymd,
      areaId: dayRow?.areaId ?? dayRow?.activeAreaId ?? employee?.areaId ?? null,
      areaName:
        dayRow?.areaName ??
        dayRow?.activeAreaName ??
        employee?.areaName ??
        null,
      activeAreaId:
        dayRow?.activeAreaId ?? dayRow?.areaId ?? employee?.areaId ?? null,
      activeAreaName:
        dayRow?.activeAreaName ??
        dayRow?.areaName ??
        employee?.areaName ??
        null,
    });
  };

  const renderAttendanceRow = (
    employee,
    areaColor = employee.areaColor,
    options = {},
  ) => {
    const { showAreaLabel = false, showTotal = true } = options;
    return (
      <Box
        key={employee.virtualKey}
        sx={{
          ...TABLE_ROW_SX,
          borderRadius: "14px",
        }}
      >
        <Box sx={EMPLOYEE_COL_SX}>
          <Box
            onClick={() => handleEmployeeClick(employee)}
            sx={{
              width: "100%",
              minHeight: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 0.6,
              py: 0.5,
              bgcolor: "neutral.200",
              borderRadius: "52px",
              boxShadow: "inset 0px 0px 4px rgba(165, 165, 178, 1)",
              cursor: "pointer",
              userSelect: "none",
              transition: "0.15s ease",
              "&:hover": {
                bgcolor: "neutral.250",
                transform: "translateY(-1px)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                bgcolor: areaColor?.main || "secondary.300",
              }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: "primary.900",
                }}
              >
                {employee.fullName || `Empleado ${employee.employeeId}`}
              </Typography>

              {showAreaLabel ? (
                <Typography
                  noWrap
                  sx={{
                    mt: 0.2,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "neutral.700",
                  }}
                >
                  Área: {employee.areaName}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box sx={DAYS_COL_SX}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
            {weekHeaders.map((day) => {
              const dayRow = employee.rowsByDay.get(day.ymd) || null;
              const cell = getCellConfigForArea(dayRow, employee.areaId, currentTime);

              return (
                <Box
                  key={`${employee.virtualKey}-${day.ymd}`}
                  onClick={() => handleAttendanceCellClick(employee, day, dayRow)}
                  sx={{
                    minHeight: "6vh",
                    borderRadius: "10px",
                    bgcolor: cell.bgcolor,
                    border: "2px solid",
                    borderColor: cell.borderColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.55,
                    py: 0.35,
                    overflow: "hidden",
                    cursor: "pointer",
                    opacity: dayRow ? 1 : 0.82,
                    transition: "background-color 0.15s, transform 0.15s, filter 0.15s",
                    "&:hover": {
                      filter: "brightness(1.05)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Stack direction="row" spacing={0.625} alignItems="center" sx={{ maxWidth: "100%" }}>
                    {cell.warning ? (
                      <Tooltip title="Jornada inusual" arrow>
                        <WarningRoundedIcon
                          aria-label="Jornada inusual"
                          sx={{
                            fontSize: 18,
                            color: "#FFFFFF",
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    ) : null}
                    {cell.open ? (
                      <Tooltip title="En curso" arrow>
                        <Box
                          component="span"
                          role="img"
                          aria-label="Asistencia en curso"
                          sx={{
                            width: 18,
                            height: 18,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "logoblanco",
                            flexShrink: 0,
                          }}
                        >
                          <RepeatingTimerIcon
                            aria-hidden="true"
                            size={18}
                            style={{ display: "inline-flex" }}
                          />
                        </Box>
                      </Tooltip>
                    ) : null}
                    <Typography
                      noWrap
                      sx={{
                        maxWidth: "100%",
                        fontSize: 10.5,
                        fontWeight: 900,
                        color: cell.color,
                        lineHeight: 1.05,
                        textAlign: "center",
                      }}
                    >
                      {cell.label}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>

        {showTotal && (
          <Box sx={TOTAL_COL_SX}>
            <Box
              sx={{
                ...TOTAL_VALUE_BOX_SX,
                minHeight: "6vh",
                height: "100%",
                borderRadius: "10px",
                bgcolor: "rgba(170, 176, 193, 0.85)",
                border: "1px solid rgba(96,96,113,0.18)",
                boxShadow: "inset 0px 0px 3px rgba(96,96,113,0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.6,
                py: 0.35,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: "primary.900",
                  textAlign: "center",
                  lineHeight: 1.05,
                }}
              >
                {formatMinutesLabel(employee.totalMinutes)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderTotalRow = () => (
    <Box
      sx={{
        ...TABLE_ROW_SX,
        ...TABLE_SIDE_PADDING_SX,
        pt: 0.5,
        borderTop: "1px solid rgba(96,96,113,0.14)",
        bgcolor: "neutral.50",
      }}
    >
      <Box sx={EMPLOYEE_COL_SX}>
        <Box
          sx={{
            width: "100%",
            minHeight: "6vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1,
            py: 0.5,
            bgcolor: "neutral.600",
            borderRadius: "34px",
            boxShadow: "inset 0px 0px 8px rgba(75, 75, 88, 1)",
          }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: 13,
              fontWeight: 900,
              color: "logoblanco",
              textAlign: "center",
            }}
          >
            Total
          </Typography>
        </Box>
      </Box>

      <Box sx={DAYS_COL_SX}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
          {weeklyTotals.byDay.map((minutes, index) => (
            <Box
              key={weekHeaders[index]?.ymd || index}
              sx={{
                minHeight: "6vh",
                borderRadius: "10px",
                bgcolor: "rgba(170, 176, 193, 0.85)",
                border: "1px solid rgba(96,96,113,0.18)",
                boxShadow: "inset 0px 0px 3px rgba(96,96,113,0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 0.55,
                py: 0.35,
                overflow: "hidden",
              }}
            >
              <Typography
                noWrap
                sx={{
                  maxWidth: "100%",
                  fontSize: 11,
                  fontWeight: 900,
                  color: "primary.900",
                  textAlign: "center",
                }}
              >
                {formatMinutesLabel(minutes)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={TOTAL_COL_SX}>
        <Box
          sx={{
            ...TOTAL_VALUE_BOX_SX,
            minHeight: "6vh",
            height: "100%",
            borderRadius: "10px",
            bgcolor: "rgba(170, 176, 193, 0.85)",
            border: "1px solid rgba(96,96,113,0.18)",
            boxShadow: "inset 0px 0px 3px rgba(96,96,113,0.28)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 0.6,
            py: 0.35,
          }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: 11,
              fontWeight: 900,
              color: "primary.900",
              textAlign: "center",
            }}
          >
            {formatMinutesLabel(weeklyTotals.totalMinutes)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderAreaGroup = (group) => {
    const areaColor = group.areaColor || getAreaColor(group.areaId);

    return (
      <Box
        key={group.areaId}
        sx={{
          width: "100%",
          borderRadius: "18px",
          bgcolor: areaColor.soft,
          border: `1px solid ${areaColor.border}`,
          p: 0.75,
          boxShadow: "inset 0px 0px 3px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, width: "100%", mb: 0.75 }}>
          <Box sx={EMPLOYEE_COL_SX}>
            <Box
              sx={{
                height: 26,
                borderRadius: "8px",
                px: 1.4,
                display: "flex",
                alignItems: "center",
                bgcolor: areaColor.main,
                boxShadow: "inset 0px 0px 3px rgba(0,0,0,0.12)",
              }}
            >
              <Typography
                noWrap
                sx={{
                  color: "#0F172A",
                  fontSize: 13,
                  fontWeight: 900,
                  lineHeight: 1,
                }}
              >
                {group.areaName}
              </Typography>
            </Box>
          </Box>

          <Box sx={DAYS_COL_SX} />

          <Box sx={TOTAL_COL_SX}>
            <Box
              sx={{
                ...TOTAL_VALUE_BOX_SX,
                height: 26,
                borderRadius: "8px",
                px: 0.8,
                bgcolor: areaColor.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "inset 0px 0px 3px rgba(0,0,0,0.12)",
              }}
            >
              <Typography
                noWrap
                sx={{
                  color: "#0F172A",
                  fontSize: 11,
                  fontWeight: 900,
                  lineHeight: 1,
                }}
              >
                {formatMinutesLabel(group.totalMinutes)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Stack spacing={0.55}>
          {group.rows.map((employee) =>
            renderAttendanceRow(employee, areaColor)
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          p: 2,
          bgcolor: "neutral.300",
          borderRadius: "30px",
          boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
          width: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            position: "absolute",
            top: 18,
            right: 36,
            zIndex: 3,
          }}
        >
          <Tooltip title="Estados de asistencia">
            <IconButton
              onClick={() => setStatesInfoOpen(true)}
              aria-label="Estados de asistencia"
              sx={{
                width: 34,
                height: 34,
                bgcolor: "neutral.50",
                borderRadius: "0px",
                "&:hover": { bgcolor: "neutral.100" },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 25, color: "primary.900" }} />
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            aria-label="Más opciones"
            sx={{
              width: 34,
              height: 34,
              bgcolor: "neutral.50",
              borderRadius: "0px",
              "&:hover": { bgcolor: "neutral.100" },
            }}
          >
            <MoreHorizIcon sx={{ fontSize: 28, color: "primary.900" }} />
          </IconButton>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { borderRadius: "14px", minWidth: 210 } }}
        >
          <MenuItem
            disabled={exportingPdf || loading}
            onClick={() => {
              setMenuAnchor(null);
              onOpenExportPdf?.();
            }}
          >
            <ListItemIcon>
              <PictureAsPdfIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary={exportingPdf ? "Exportando PDF..." : "Exportar PDF"} />
          </MenuItem>
        </Menu>

        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
          <AttendanceToolbar
            weekLabel={weekLabel}
            onPrevWeek={onPrevWeek}
            onNextWeek={onNextWeek}
          />

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ width: "100%", flexWrap: "wrap", rowGap: 1 }}
          >
            <TextField
              value={search}
              onChange={(e) => onSearch?.(e.target.value)}
              placeholder="Buscar por nombre o DNI..."
              size="small"
              sx={{
                flex: 1,
                width: "50%",
                ...PILL_SX,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  height: "100%",
                  fontSize: 13,
                },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "primary.900", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small">
              <Select
                displayEmpty
                value={branchId}
                onChange={(e) => onBranchId?.(e.target.value)}
                sx={SELECT_SX}
                renderValue={(value) => {
                  if (!value) {
                    return <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Sucursal</Typography>;
                  }

                  const found = branches.find((branch) => String(branch.id) === String(value));
                  return <Typography sx={{ fontSize: 13 }}>{found?.name ?? value}</Typography>;
                }}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>

                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <Select
                displayEmpty
                value={areaId}
                onChange={(e) => onAreaId?.(e.target.value)}
                sx={SELECT_SX}
                renderValue={(value) => {
                  if (!value) {
                    return <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Área</Typography>;
                  }

                  const found = areas.find((area) => String(area.id) === String(value));
                  return <Typography sx={{ fontSize: 13 }}>{found?.name ?? value}</Typography>;
                }}
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>

                {areas.map((area) => (
                  <MenuItem key={area.id} value={area.id}>
                    {area.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <AttendanceViewSwitch
              value={viewMode}
              onChange={setViewMode}
              sortDirection={sortDirection}
              onToggleSortDirection={() =>
                setSortDirection((value) => (value === "desc" ? "asc" : "desc"))
              }
            />
          </Stack>

          <Box
            sx={{
              p: 1,
              bgcolor: "neutral.50",
              borderRadius: "24px",
              boxShadow:
                "inset 0px 0px 2px rgba(96, 96, 113, 0.22), 0px 8px 18px rgba(0,0,0,0.04)",
              overflow: "hidden",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {fetchError ? (
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "error.main", mb: 1 }}>
                {fetchError}
              </Typography>
            ) : null}

            <Box
              sx={{
                ...TABLE_ROW_SX,
                ...TABLE_SIDE_PADDING_SX,
              }}
            >
              <Box sx={EMPLOYEE_COL_SX}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: "100%",
                    height: "5.6vh",
                    px: 2.5,
                    py: 0.5,
                    bgcolor: "neutral.600",
                    borderRadius: "34px",
                    boxShadow: "inset 0px 0px 8px rgba(75, 75, 88, 1)",
                  }}
                >
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "logoblanco" }}>
                    Empleados
                  </Typography>
                </Stack>
              </Box>

              <Box sx={DAYS_COL_SX}>
                <Box
                  sx={{
                    px: 1,
                    bgcolor: "neutral.600",
                    borderRadius: "15px",
                    boxShadow: "inset 0px 0px 8px rgba(75, 75, 88, 1)",
                    height: "5.6vh",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, width: "100%" }}>
                    {weekHeaders.map((day) => (
                      <Typography
                        key={day.ymd}
                        sx={{
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "logoblanco",
                        }}
                      >
                        {day.label}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Box sx={TOTAL_COL_SX}>
                <Box
                  sx={{
                    width: "100%",
                    height: "5.6vh",
                    px: 1,
                    py: 0.5,
                    bgcolor: "neutral.600",
                    borderRadius: "14px",
                    boxShadow: "inset 0px 0px 8px rgba(75, 75, 88, 1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: "center",
                      fontSize: "13px",
                      fontWeight: 800,
                      color: "logoblanco",
                    }}
                  >
                    Total
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                mt: 1,
                overflowY: "scroll",
                scrollbarGutter: "stable",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(17,24,39,.5) transparent",
                "&::-webkit-scrollbar": { width: "10px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(17,24,39,.25)",
                  borderRadius: "999px",
                  border: "3px solid transparent",
                  backgroundClip: "content-box",
                },
              }}
            >
              <Stack spacing={1} sx={{ width: "100%" }}>
                {loading ? (
                  <Typography sx={{ fontSize: 14, color: "neutral.700", fontWeight: 600, py: 2 }}>
                    Cargando asistencia semanal...
                  </Typography>
                ) : null}

                {!loading && virtualRows.length === 0 ? (
                  <Typography sx={{ fontSize: 14, color: "neutral.700", fontWeight: 600, py: 2 }}>
                    No hay empleados para mostrar con los filtros actuales.
                  </Typography>
                ) : null}

                {!loading && viewMode === "area"
                  ? areaGroups.map(renderAreaGroup)
                  : null}

                {!loading && viewMode === "hours" && hourRows.length > 0 ? (
                  <Box sx={{ p: 0.75 }}>
                    <Stack spacing={0.55}>
                      {hourRows.map((employee) =>
                        renderAttendanceRow(employee, employee.areaColor, {
                          showAreaLabel: true,
                        })
                      )}
                    </Stack>
                  </Box>
                ) : null}
              </Stack>
            </Box>

            {!loading && virtualRows.length > 0 ? renderTotalRow() : null}
          </Box>
        </Stack>
      </Box>

      <AttendanceDayDetailModal
        open={!!selectedDayRow}
        row={selectedDayRow}
        employeeName={selectedDayRow?.fullName}
        onClose={() => setSelectedDayRow(null)}
        onSaved={(result) => {
          if (!result?.keepOpen) setSelectedDayRow(null);
          onAttendanceSaved?.();
        }}
      />

      <AttendanceStatesDialog
        open={statesInfoOpen}
        onClose={() => setStatesInfoOpen(false)}
      />
    </>
  );
}
